(() => {
  document.querySelectorAll('[data-rinleaf-cart-submit]').forEach((button) => {
    button.removeAttribute('data-atc-form');
  });

  const selectors = {
    productForm: '[data-rinleaf-product-form]',
    submitButton: '[data-rinleaf-cart-submit]',
    quantityInput: '[data-rinleaf-quantity-input]',
    drawerRoot: '[data-rinleaf-cart-drawer]',
    pageRoot: '[data-rinleaf-cart-page]',
    cartLine: '[data-rinleaf-cart-line]',
    lineInput: '[data-rinleaf-line-quantity]',
    lineButton: '[data-rinleaf-line-button]',
    removeButton: '[data-rinleaf-line-remove]',
    cartCount: '[data-rinleaf-cart-count], [data-cart-count]',
    cartTotal: '[data-rinleaf-cart-total]',
    noteInput: '[data-rinleaf-cart-note]',
    saveNoteButton: '[data-rinleaf-save-note]',
    saveDiscountButton: '[data-rinleaf-save-discount]',
    giftWrapButton: '[data-rinleaf-gift-wrap]',
    upsellAddButton: '[data-rinleaf-upsell-add]',
    discountInput: '[data-rinleaf-cart-discount]'
  };

  const state = {
    updating: false,
    pendingLineChange: null,
    pendingNote: null,
    pendingAddItems: [],
    discountCode: '',
    pendingRefresh: false,
    localizationKey: '',
    externalRefreshTimer: null,
    ignoreExternalRefreshUntil: 0
  };

  const INTERNAL_CART_REFRESH_GUARD_MS = 4000;

  function rootUrl() {
    return window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
  }

  function guardExternalRefresh(duration = INTERNAL_CART_REFRESH_GUARD_MS) {
    state.ignoreExternalRefreshUntil = Math.max(state.ignoreExternalRefreshUntil, Date.now() + duration);
  }

  function shouldIgnoreExternalRefresh() {
    return Date.now() < state.ignoreExternalRefreshUntil;
  }

  async function fetchCart() {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    });
    if (!response.ok) throw new Error(`cart.js failed: ${response.status}`);
    return response.json();
  }

  async function postJSON(path, payload) {
    const normalizedPath = String(path).replace(/^\/+/, '');
    const response = await fetch(`${rootUrl()}${normalizedPath}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error(`${path} failed: ${response.status}`);
    return response.json();
  }

  async function postCartChange(payload) {
    guardExternalRefresh();
    return postJSON('cart/change.js', {
      ...payload,
      sections: 'cart_data,mini_cart',
      sections_url: window.location.pathname
    });
  }

  async function postCartAddForm(formData) {
    guardExternalRefresh();
    formData.append('sections', 'cart_data,mini_cart');
    formData.append('sections_url', window.location.pathname);

    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      body: formData,
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      }
    });
    if (!response.ok) throw new Error(`cart/add.js failed: ${response.status}`);
    return response.json();
  }

  function parseHTML(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function getTargetLocalization(override) {
    const language = ((override && override.language) || document.documentElement.getAttribute('lang') || '').toLowerCase();
    const map = {
      en: { country: 'US', currency: 'USD' },
      de: { country: 'DE', currency: 'EUR' },
      fr: { country: 'FR', currency: 'EUR' },
      ko: { country: 'KR', currency: 'KRW' },
      es: { country: 'ES', currency: 'EUR' },
      it: { country: 'IT', currency: 'EUR' },
      ja: { country: 'JP', currency: 'JPY' },
      'pt-br': { country: 'BR', currency: 'BRL' },
      pt: { country: 'PT', currency: 'EUR' },
      ar: { country: 'SA', currency: 'SAR' },
      cs: { country: 'CZ', currency: 'CZK' },
      da: { country: 'DK', currency: 'DKK' },
      el: { country: 'GR', currency: 'EUR' },
      fi: { country: 'FI', currency: 'EUR' },
      nl: { country: 'NL', currency: 'EUR' },
      no: { country: 'NO', currency: 'NOK' },
      pl: { country: 'PL', currency: 'PLN' },
      ru: { country: 'RU', currency: 'RUB' },
      sv: { country: 'SE', currency: 'SEK' },
      th: { country: 'TH', currency: 'THB' },
      'zh-cn': { country: 'CN', currency: 'CNY' },
      'zh-hans': { country: 'CN', currency: 'CNY' },
      'zh-tw': { country: 'TW', currency: 'TWD' },
      'zh-hant': { country: 'TW', currency: 'TWD' },
      tr: { country: 'TR', currency: 'TRY' },
      vi: { country: 'VN', currency: 'VND' }
    };
    if (!map[language]) return null;
    return {
      language,
      country: (override && override.country) || map[language].country,
      currency: (override && override.currency) || map[language].currency
    };
  }

  async function ensureShopifyLocalization(override, force = false) {
    state.localizationKey = '';
  }

  function wait(ms) {
    return new Promise((resolve) => window.setTimeout(resolve, ms));
  }

  async function fetchSection(url) {
    const response = await fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });
    if (!response.ok) throw new Error(`section fetch failed: ${response.status}`);
    return response.text();
  }

  function replaceDrawerPart(currentRoot, nextRoot, selector, mode = 'html') {
    const current = currentRoot.querySelector(selector);
    const next = nextRoot.querySelector(selector);
    if (!current || !next) return;

    if (mode === 'replace') {
      current.replaceWith(next);
      return;
    }

    current.innerHTML = next.innerHTML;
  }

  function escapeHTML(value) {
    return String(value || '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;',
      '<': '&lt;',
      '>': '&gt;',
      '"': '&quot;',
      "'": '&#039;'
    }[character]));
  }

  function formatCartMoney(cents, cart) {
    const currency = (cart && cart.currency) || 'USD';
    const locale = document.documentElement.getAttribute('lang') || navigator.language || 'en-US';

    try {
      return new Intl.NumberFormat(locale, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency}`;
    }
  }

  function sectionMatchesExpectedCurrency(sectionHTML, override) {
    return Boolean(sectionHTML);
  }

  function cartMatchesExpectedCurrency(cart, override) {
    return true;
  }

  function cartItemURL(item) {
    const url = item.url || item.product_url || '#';
    const separator = url.includes('?') ? '&' : '?';
    return item.variant_id ? `${url}${separator}variant=${item.variant_id}` : url;
  }

  function cartItemImage(item) {
    const image = item.featured_image && item.featured_image.url ? item.featured_image.url : item.image;
    if (!image) return '';
    return String(image).replace(/(\.(jpg|jpeg|png|webp|gif))(?:\?.*)?$/i, '_240x$1');
  }

  function renderCartItemsFromCart(cart) {
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      return '<div class="rinleaf-cart-empty rinleaf-cart-empty--drawer">' + window.RinleafI18n.cartEmpty + '</div>';
    }

    return cart.items.map((item, index) => {
      const line = index + 1;
      const itemURL = escapeHTML(cartItemURL(item));
      const imageURL = escapeHTML(cartItemImage(item));
      const title = escapeHTML(item.product_title || item.title || '');
      const variantTitle = item.variant_title && item.variant_title !== 'Default Title' ? `<p class="t4s-cart_meta_variant">${escapeHTML(item.variant_title)}</p>` : '';
      const quantity = Math.max(1, Number(item.quantity || 1));
      const price = escapeHTML(formatCartMoney(item.final_line_price || item.line_price || item.price || 0, cart));
      const min = item.quantity_rule && item.quantity_rule.min ? Number(item.quantity_rule.min) : 1;
      const max = item.quantity_rule && item.quantity_rule.max ? Number(item.quantity_rule.max) : 9999;
      const step = item.quantity_rule && item.quantity_rule.increment ? Number(item.quantity_rule.increment) : 1;

      return `
<div data-rinleaf-cart-line data-line="${line}" class="t4s-mini_cart__item t4s-d-flex t4s-align-items-center t4s-pr t4s-oh">
  <a href="${itemURL}" class="t4s-mini_cart__img t4s-pr t4s-oh">${imageURL ? `<img width="120" height="120" src="${imageURL}" sizes="120px" alt="${title}">` : ''}</a>
  <div class="t4s-mini_cart__info">
    <a href="${itemURL}" class="t4s-mini_cart__title t4s-truncate">${title}</a>
    <div class="t4s-mini_cart__meta">
      ${variantTitle}
      <div class="t4s-cart_meta_price"><div class="t4s-cart_price"><span class="money">${price}</span></div></div>
    </div>
    <div class="t4s-mini_cart__actions">
      <div data-quantity-wrapper class="t4s-quantity-wrapper t4s-quantity-cart-item">
        <button type="button" data-rinleaf-line-button data-change="-1" class="t4s-quantity-selector is--minus" aria-label="${window.RinleafI18n.quantity}">
          <svg focusable="false" class="icon icon--minus" viewBox="0 0 10 2" role="presentation"><path d="M10 0v2H0V0z" fill="currentColor"></path></svg>
        </button>
        <input data-rinleaf-line-quantity type="number" id="miniupdates_${escapeHTML(item.key || line)}" data-id="${escapeHTML(item.key || '')}" data-line="${line}" class="t4s-quantity-input" step="${step}" min="${min}" max="${max}" name="updates[]" value="${quantity}" size="4" pattern="[0-9]*" inputmode="numeric" aria-label="${window.RinleafI18n.quantity}">
        <button type="button" data-rinleaf-line-button data-change="1" class="t4s-quantity-selector is--plus" aria-label="${window.RinleafI18n.quantity}">
          <svg focusable="false" class="icon icon--plus" viewBox="0 0 10 10" role="presentation"><path d="M6 4h4v2H6v4H4V6H0V4h4V0h2v4z" fill="currentColor" fill-rule="evenodd"></path></svg>
        </button>
      </div>
      <a href="${rootUrl()}cart/change?line=${line}&amp;quantity=0" rel="nofollow" class="t4s-mini_cart__remove" data-rinleaf-line-remove data-line="${line}" aria-label="${window.RinleafI18n.removeItem}">
        <svg viewBox="0 0 24 24" width="17"><use href="#icon-cart-remove"/></svg>
      </a>
    </div>
  </div>
</div>`;
    }).join('');
  }

  function ensureDrawerItemsMatchCart(cart, localizationOverride) {
    const itemsContainer = document.querySelector('#t4s-mini_cart [data-rinleaf-cart-items], #t4s-mini_cart [data-cart-items]');
    if (!itemsContainer || !cart) return;
    if (!cartMatchesExpectedCurrency(cart, localizationOverride)) return;

    const renderedLines = itemsContainer.querySelectorAll(selectors.cartLine).length;
    if (Number(cart.item_count || 0) > 0 && renderedLines === 0) {
      itemsContainer.innerHTML = renderCartItemsFromCart(cart);
    }
  }

  function miniCartSectionUrl() {
    const separator = window.location.pathname.includes('?') ? '&' : '?';
    return `${window.location.pathname}${separator}section_id=mini_cart&_rinleaf_cart_ts=${Date.now()}`;
  }

  function extractCartCountFromSectionData(sectionHTML) {
    if (!sectionHTML) return null;
    const match = String(sectionHTML).match(/\[t4s_split1\]\s*(\d+)\s*\[t4s_split2\]/);
    if (!match) return null;
    const nextCount = Number(match[1]);
    return Number.isFinite(nextCount) ? nextCount : null;
  }

  function extractDrawerCountFromHTML(html) {
    if (!html) return null;
    const doc = parseHTML(html);
    const wrapper = doc.getElementById('t4s-mini_cart') || doc.querySelector('[data-cart-wrapper]');
    if (!wrapper) return null;
    const nextCount = Number(wrapper.getAttribute('data-ccount'));
    return Number.isFinite(nextCount) ? nextCount : null;
  }

  function renderDrawerHTML(html, expectedCart, localizationOverride) {
    const doc = parseHTML(html);
    const currentWrapper = document.getElementById('t4s-mini_cart');
    const nextWrapper = doc.getElementById('t4s-mini_cart');
    const nextContent = nextWrapper ? nextWrapper.innerHTML : doc.body.innerHTML;
    const nextCount = nextWrapper ? nextWrapper.getAttribute('data-ccount') : null;

    if (currentWrapper && nextContent.trim()) {
      replaceDrawerPart(currentWrapper, nextWrapper || doc, '[data-rinleaf-cart-items], [data-cart-items]');
      replaceDrawerPart(currentWrapper, nextWrapper || doc, '[data-cart-prices]');
      replaceDrawerPart(currentWrapper, nextWrapper || doc, '[data-cart-discounts]');
      if (nextCount !== null) currentWrapper.setAttribute('data-ccount', nextCount);
      ensureDrawerItemsMatchCart(expectedCart, localizationOverride);
      return nextCount !== null ? Number(nextCount) : null;
    }

    const current = document.querySelector(selectors.drawerRoot);
    const next = doc.querySelector(selectors.drawerRoot);
    if (current && next) {
      current.replaceWith(next);
    }

    return nextCount !== null ? Number(nextCount) : null;
  }

  async function refreshDrawer(expectedCart, attempt = 0, sectionHTML = '', localizationOverride) {
    if (sectionHTML) {
      return renderDrawerHTML(sectionHTML, expectedCart, localizationOverride);
    }

    const html = await fetchSection(miniCartSectionUrl());
    const doc = parseHTML(html);
    const nextWrapper = doc.getElementById('t4s-mini_cart');
    const expectedCount = expectedCart && Number(expectedCart.item_count);
    const nextCount = nextWrapper ? Number(nextWrapper.getAttribute('data-ccount')) : NaN;

    if (Number.isFinite(expectedCount) && Number.isFinite(nextCount) && nextCount !== expectedCount && attempt < 3) {
      await wait(120 * (attempt + 1));
      return refreshDrawer(expectedCart, attempt + 1, '', localizationOverride);
    }

    return renderDrawerHTML(html, expectedCart, localizationOverride);
  }

  async function refreshCartPage(expectedCart, attempt = 0) {
    const current = document.querySelector(selectors.pageRoot);
    if (!current) return;
    const html = await fetchSection(`${rootUrl()}cart?section_id=${window.cartT4SectionID}&_rinleaf_cart_ts=${Date.now()}`);
    const doc = parseHTML(html);
    const next = doc.querySelector(selectors.pageRoot);
    const expectedCount = expectedCart && Number(expectedCart.item_count);
    const nextCountSource = doc.querySelector('[data-cart-wrapper], #t4s-mini_cart');
    const nextCount = nextCountSource ? Number(nextCountSource.getAttribute('data-ccount')) : NaN;

    if (Number.isFinite(expectedCount) && Number.isFinite(nextCount) && nextCount !== expectedCount && attempt < 3) {
      await wait(120 * (attempt + 1));
      return refreshCartPage(expectedCart, attempt + 1);
    }

    if (next) current.replaceWith(next);
  }

  function refreshBadgeFromCount(itemCount) {
    if (!Number.isFinite(itemCount)) return;

    document.querySelectorAll(selectors.cartCount).forEach((node) => {
      node.textContent = String(itemCount);
    });

    document.querySelectorAll('[data-cart-wrapper], #t4s-mini_cart').forEach((node) => {
      node.setAttribute('data-ccount', String(itemCount));
    });

    document.documentElement.classList.remove(...Array.from(document.documentElement.classList).filter((name) => name.startsWith('t4s-cart-count-')));
    document.documentElement.classList.add(`t4s-cart-count-${itemCount}`);
  }

  function refreshBadge(cart) {
    refreshBadgeFromCount(Number(cart.item_count || 0));
  }

  function openCartDrawer() {
    const drawer = document.getElementById('t4s-mini_cart');
    if (!drawer) return;

    if (window.T4SThemeSP && window.T4SThemeSP.Drawer && typeof window.T4SThemeSP.Drawer.opend === 'function' && window.jQuery_T4NT) {
      window.T4SThemeSP.Drawer.opend(window.jQuery_T4NT(drawer));
      return;
    }

    drawer.setAttribute('aria-hidden', 'false');
  }

  function syncDiscountInputs(value = state.discountCode) {
    state.discountCode = String(value || '');
    document.querySelectorAll(selectors.discountInput).forEach((input) => {
      if (input.value !== state.discountCode) {
        input.value = state.discountCode;
      }
    });
  }

  function getCartRoots() {
    return Array.from(document.querySelectorAll(`${selectors.drawerRoot}, ${selectors.pageRoot}`));
  }

  function getLineByNumber(line) {
    return document.querySelector(`${selectors.cartLine}[data-line="${line}"]`);
  }

  function setCartUpdating(line) {
    getCartRoots().forEach((root) => {
      root.classList.add('is-rinleaf-cart-updating');
      root.setAttribute('aria-busy', 'true');
    });

    const activeLine = getLineByNumber(line);
    if (!activeLine) return;

    activeLine.classList.add('is-rinleaf-updating');
    activeLine.setAttribute('aria-busy', 'true');
    activeLine.querySelectorAll(`${selectors.lineButton}, ${selectors.removeButton}, ${selectors.lineInput}`).forEach((control) => {
      control.disabled = true;
      control.setAttribute('aria-disabled', 'true');
    });
  }

  function clearCartUpdating() {
    getCartRoots().forEach((root) => {
      root.classList.remove('is-rinleaf-cart-updating');
      root.removeAttribute('aria-busy');
    });

    document.querySelectorAll(`${selectors.cartLine}.is-rinleaf-updating`).forEach((line) => {
      line.classList.remove('is-rinleaf-updating');
      line.removeAttribute('aria-busy');
      line.querySelectorAll(`${selectors.lineButton}, ${selectors.removeButton}, ${selectors.lineInput}`).forEach((control) => {
        control.disabled = false;
        control.removeAttribute('aria-disabled');
      });
    });
  }

  async function refreshCartUI(localizationOverride, sections, force = false) {
    if (state.updating && !force) {
      state.pendingRefresh = true;
      return fetchCart();
    }

    await ensureShopifyLocalization(localizationOverride);

    const miniCartHTML = sectionMatchesExpectedCurrency(sections && sections.mini_cart, localizationOverride) ? sections && sections.mini_cart : '';
    const sectionCount = extractCartCountFromSectionData(sections && sections.cart_data);
    const pageRoot = document.querySelector(selectors.pageRoot);

    if (miniCartHTML && !pageRoot) {
      const nextCount = await refreshDrawer(null, 0, miniCartHTML, localizationOverride);
      refreshBadgeFromCount(Number.isFinite(sectionCount) ? sectionCount : nextCount);
      syncDiscountInputs();
      return { item_count: Number.isFinite(sectionCount) ? sectionCount : (Number.isFinite(nextCount) ? nextCount : 0) };
    }

    const cart = await fetchCart();
    await Promise.all([
      refreshDrawer(cart, 0, miniCartHTML, localizationOverride),
      refreshCartPage(cart)
    ]);
    refreshBadge(cart);
    syncDiscountInputs();
    return cart;
  }

  async function flushPendingUpdates() {
    if (state.updating) return;

    if (state.pendingLineChange) {
      const pendingChange = state.pendingLineChange;
      state.pendingLineChange = null;
      await changeLine(pendingChange.line, pendingChange.quantity);
      return;
    }

    if (state.pendingNote !== null) {
      const pendingNote = state.pendingNote;
      state.pendingNote = null;
      await saveNote(pendingNote);
      return;
    }

    if (state.pendingAddItems.length > 0) {
      const pendingItems = state.pendingAddItems.splice(0);
      for (const item of pendingItems) {
        await addVariant(item.id, item.quantity);
      }
      return;
    }

    if (state.pendingRefresh) {
      state.pendingRefresh = false;
      await refreshCartUI();
    }
  }

  async function flushPendingLineChange() {
    await flushPendingUpdates();
  }

  function scheduleExternalCartRefresh() {
    window.clearTimeout(state.externalRefreshTimer);
    state.externalRefreshTimer = window.setTimeout(() => {
      refreshCartUI().catch(console.error);
    }, 80);
  }

  async function handleProductSubmit(event) {
    const form = event.target.closest(selectors.productForm);
    if (!form) return;
    event.preventDefault();
    if (state.updating) return;

    state.updating = true;
    let shouldOpenDrawer = false;
    try {
      await ensureShopifyLocalization(null, true);
      const formData = new FormData(form);
      const response = await postCartAddForm(formData);
      await refreshCartUI(null, response.sections, true);
      shouldOpenDrawer = true;
    } finally {
      state.updating = false;
      await flushPendingUpdates();
      if (shouldOpenDrawer) {
        openCartDrawer();
        window.setTimeout(openCartDrawer, 120);
      }
    }
  }

  async function changeLine(line, quantity) {
    setCartUpdating(line);

    if (state.updating) {
      state.pendingLineChange = { line, quantity };
      return;
    }

    state.updating = true;
    try {
      let nextChange = { line, quantity };

      while (nextChange) {
        const response = await postCartChange(nextChange);
        await refreshCartUI(null, response.sections, true);

        if (state.pendingLineChange) {
          nextChange = state.pendingLineChange;
          state.pendingLineChange = null;
        } else {
          nextChange = null;
        }
      }
    } finally {
      state.updating = false;
      clearCartUpdating();
      await flushPendingUpdates();
    }
  }

  async function saveNote(note) {
    if (state.updating) {
      state.pendingNote = note;
      return;
    }

    state.updating = true;
    try {
      let nextNote = note;

      while (nextNote !== null) {
        guardExternalRefresh();
        await postJSON('cart/update.js', { note: nextNote });
        await refreshCartUI();

        if (state.pendingNote !== null) {
          nextNote = state.pendingNote;
          state.pendingNote = null;
        } else {
          nextNote = null;
        }
      }
    } finally {
      state.updating = false;
      await flushPendingUpdates();
    }
  }

  async function addVariant(variantId, quantity = 1) {
    const normalizedVariantId = Number(variantId);
    const normalizedQuantity = Math.max(1, Number(quantity || '1'));

    if (!normalizedVariantId) return;

    if (state.updating) {
      state.pendingAddItems.push({ id: normalizedVariantId, quantity: normalizedQuantity });
      return;
    }

    state.updating = true;
    try {
      await ensureShopifyLocalization(null, true);
      let nextItems = [{ id: normalizedVariantId, quantity: normalizedQuantity }];

      while (nextItems.length > 0) {
        const nextItem = nextItems.shift();
        const formData = new FormData();
        formData.append('id', String(nextItem.id));
        formData.append('quantity', String(nextItem.quantity));

        const response = await postCartAddForm(formData);
        await refreshCartUI(null, response.sections, true);

        if (state.pendingAddItems.length > 0) {
          nextItems = nextItems.concat(state.pendingAddItems.splice(0));
        }
      }
    } finally {
      state.updating = false;
      await flushPendingUpdates();
    }
  }

  document.addEventListener('submit', (event) => {
    if (event.target.matches(selectors.productForm)) {
      handleProductSubmit(event).catch(console.error);
    }
  });

  document.addEventListener('click', (event) => {
    const removeButton = event.target.closest(selectors.removeButton);
    if (removeButton) {
      event.preventDefault();
      changeLine(Number(removeButton.dataset.line), 0).catch(console.error);
      return;
    }

    const saveNoteButton = event.target.closest(selectors.saveNoteButton);
    if (saveNoteButton) {
      const noteContainer = saveNoteButton.closest('.t4s-mini_cart-tool__content');
      const noteInput = noteContainer ? noteContainer.querySelector(selectors.noteInput) : null;
      if (noteInput) {
        saveNote(noteInput.value).catch(console.error);
      }
      return;
    }

    const saveDiscountButton = event.target.closest(selectors.saveDiscountButton);
    if (saveDiscountButton) {
      const discountContainer = saveDiscountButton.closest('.t4s-mini_cart-tool__content');
      const discountInput = discountContainer ? discountContainer.querySelector(selectors.discountInput) : null;
      if (discountInput) {
        syncDiscountInputs(discountInput.value);
      }
      return;
    }

    const giftWrapButton = event.target.closest(selectors.giftWrapButton);
    if (giftWrapButton) {
      event.preventDefault();
      addVariant(giftWrapButton.dataset.variantId, 1).catch(console.error);
      return;
    }

    const upsellAddButton = event.target.closest(selectors.upsellAddButton);
    if (upsellAddButton) {
      event.preventDefault();
      addVariant(upsellAddButton.dataset.variantId, 1).catch(console.error);
      return;
    }

    const productQuantityButton = event.target.closest('[data-quantity-selector]');
    if (productQuantityButton) {
      const form = productQuantityButton.closest(selectors.productForm);
      const input = form ? form.querySelector(selectors.quantityInput) : null;
      if (input) {
        event.preventDefault();
        const step = Math.max(1, Number(input.getAttribute('step') || '1'));
        const min = Math.max(1, Number(input.getAttribute('min') || '1'));
        const max = Number(input.getAttribute('max') || '');
        const change = productQuantityButton.hasAttribute('data-decrease-qty') ? -step : step;
        const nextValue = Math.max(min, Number(input.value || String(min)) + change);
        input.value = Number.isFinite(max) && max > 0 ? String(Math.min(max, nextValue)) : String(nextValue);
      }
      return;
    }

    const changeButton = event.target.closest(selectors.lineButton);
    if (changeButton) {
      event.preventDefault();
      const input = changeButton.parentElement.querySelector(selectors.lineInput);
      const line = Number(input.dataset.line);
      const nextValue = Math.max(0, Number(input.value || '1') + Number(changeButton.dataset.change || '0'));
      input.value = String(nextValue);
      changeLine(line, nextValue).catch(console.error);
    }
  });

  document.addEventListener('change', (event) => {
    const noteInput = event.target.closest(selectors.noteInput);
    if (!noteInput) return;
    saveNote(noteInput.value).catch(console.error);
  });

  document.addEventListener('change', (event) => {
    const discountInput = event.target.closest(selectors.discountInput);
    if (!discountInput) return;
    syncDiscountInputs(discountInput.value);
  });

  document.addEventListener('change', (event) => {
    const input = event.target.closest(selectors.lineInput);
    if (!input) return;
    const line = Number(input.dataset.line);
    const quantity = Math.max(1, Number(input.value || '1'));
    input.value = String(quantity);
    changeLine(line, quantity).catch(console.error);
  });

  document.addEventListener('rinleaf:currency-context-changed', (event) => {
    refreshCartUI(null).catch(console.error);
  });

  if (window.jQuery_T4NT) {
    window.jQuery_T4NT(document).on('cart:updated', () => {
      if (!state.updating && !shouldIgnoreExternalRefresh()) scheduleExternalCartRefresh();
    });
  }

  window.RinleafCartDawn = {
    selectors,
    state,
    fetchCart,
    postJSON,
    refreshDrawer,
    refreshCartPage,
    refreshBadge,
    refreshCartUI,
    ensureShopifyLocalization,
    flushPendingUpdates,
    flushPendingLineChange,
    handleProductSubmit,
    changeLine,
    saveNote,
    addVariant,
    syncDiscountInputs,
    scheduleExternalCartRefresh,
    openCartDrawer
  };
})();

